import { addDays, differenceInCalendarDays, startOfDay } from 'date-fns';
import type { EpcScheduleTaskRow } from '../../types/scheduleTask';
import { calendarSpanInclusive, parseTaskDay } from './epcScheduleDatesDisplay';
import { parseSuccessorLinks, resolveSuccessorTaskIndex } from './epcScheduleDependencies';
import { plannedTodayPercent, taskPercent } from './taskStatus';

/** Earned value in calendar days: % × inclusive span, rounded to 1 decimal. Null without both dates. */
export function earnedDurationDays(task: EpcScheduleTaskRow): number | null {
  const span = calendarSpanInclusive(task.start, task.finish);
  if (span == null) return null;
  return Math.round((span * taskPercent(task)) / 10) / 10;
}

/** Actual % − planned-today %, rounded. Null when planned-today is unknown. */
export function progressVariancePct(task: EpcScheduleTaskRow, today: Date): number | null {
  const planned = plannedTodayPercent(task, today);
  if (planned == null) return null;
  return Math.round(taskPercent(task) - planned);
}

/** Naive linear forecast: keep the achieved %/day rate. Null unless 0 < % < 100 and start is set. */
export function naiveForecastFinish(task: EpcScheduleTaskRow, today: Date): Date | null {
  const s = parseTaskDay(task.start);
  const pct = taskPercent(task);
  if (!s || pct <= 0 || pct >= 100) return null;
  const day = startOfDay(today);
  const elapsed = Math.max(1, differenceInCalendarDays(day, startOfDay(s)) + 1);
  const rate = pct / elapsed;
  return addDays(day, Math.ceil((100 - pct) / rate));
}

export type FsSuccessorFloat = { successorIndex: number; days: number; critical: boolean };

/** Calendar gap between this task's finish and the first resolvable FS successor's start. */
export function floatToFirstFsSuccessor(
  tasks: EpcScheduleTaskRow[],
  index: number
): FsSuccessorFloat | null {
  const task = tasks[index];
  if (!task) return null;
  const finish = parseTaskDay(task.finish);
  if (!finish) return null;
  for (const link of parseSuccessorLinks(task.successors)) {
    if (link.linkTypeCode !== 'FS') continue;
    const si = resolveSuccessorTaskIndex(link, tasks, index);
    if (si == null) continue;
    const succStart = parseTaskDay(tasks[si]?.start);
    if (!succStart) continue;
    const days = Math.max(0, differenceInCalendarDays(succStart, finish) - 1);
    return { successorIndex: si, days, critical: days <= 0 };
  }
  return null;
}
